/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';

import { COMMON_ANALYTICS_ATTRIBUTES } from '../../common/constants.tsx';

const headingMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.customer-service-section.heading-csm',
	defaultMessage: 'Customer Service Management',
	description: 'Navigation group heading for customer service settings items',
});

const customerAccessItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.customer-service-section.customer-access-item',
	defaultMessage: 'Customer access',
	description:
		'Navigation item which links to a customer service access configuration settings page',
});

const chatItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.customer-service-section.chat-item',
	defaultMessage: 'Chat',
	description: 'Navigation item which links to a customer service chat configuration settings page',
});

const storageManagementItemMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.customer-service-section.storage-management-item',
	defaultMessage: 'Storage management',
	description:
		'Navigation item which links to the Customer Service Management storage management page',
});

const betaLozengeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.customer-service-section.beta-lozenge',
	defaultMessage: 'Beta',
	description: 'Beta lozenge for chat menu item in customer service section',
});

const CustomerServiceSection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(headingMessage)}</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{
						...COMMON_ANALYTICS_ATTRIBUTES,
						section: 'jiraCustomerService',
						itemId: 'customerAccess',
					}}
					isSelected={currentRoute.name === 'products-jcs-customer-access'}
					href="/jira/settings/products/customerservice/customer-access"
				>
					{formatMessage(customerAccessItemMessage)}
				</LinkMenuItem>
				{expVal('jsmgrowth-267_enforce_storage_limits_servco_free', 'isEnabled', false) && (
					<LinkMenuItem
						analytics={{
							...COMMON_ANALYTICS_ATTRIBUTES,
							section: 'jiraCustomerService',
							itemId: 'storageManagement',
						}}
						isSelected={currentRoute.name === 'products-jcs-storage-management'}
						href="/jira/settings/products/customerservice/storage-management"
					>
						{formatMessage(storageManagementItemMessage)}
					</LinkMenuItem>
				)}
				{fg('csm-omni-channel-live-chat-in-widget') && (
					<LinkMenuItem
						analytics={{
							...COMMON_ANALYTICS_ATTRIBUTES,
							section: 'jiraCustomerService',
							itemId: 'chat',
						}}
						isSelected={currentRoute.name === 'products-csm-chat'}
						href="/jira/settings/products/customerservice/chat"
						elemAfter={
							!fg('csm-omni-hide-beta-lozenge') ? (
								<Lozenge appearance="discovery">{formatMessage(betaLozengeMessage)}</Lozenge>
							) : undefined
						}
					>
						{formatMessage(chatItemMessage)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

const ExportedComponent: React.MemoExoticComponent<() => React.JSX.Element> =
	memo(CustomerServiceSection);
export default ExportedComponent;
